import { useEffect, useState } from "react";
import { supabase, getSupabaseUrl, getSupabaseAnonKey } from "@/integrations/supabase/client";
import { authRedirectUrl } from "@/shell/authRedirect";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { Loader2 } from "lucide-react";
import { useI18n } from "@/i18n/I18nContext";

/**
 * OAuth sign-in buttons, discovered from Supabase Auth itself.
 *
 * GoTrue publishes which providers are enabled at /auth/v1/settings
 * (public, anon key). The button list follows that configuration, so
 * enabling a provider in the Supabase dashboard (Microsoft/Azure, Apple,
 * and the rest) makes its button appear here with zero code changes, and
 * a provider that is not configured never renders a dead button.
 *
 * If discovery is unreachable, the four long-standing providers render as
 * the safe baseline; Microsoft and Apple appear only on confirmed
 * configuration (never optimistically).
 */
type OAuthProvider = "google" | "github" | "gitlab" | "bitbucket" | "azure" | "apple";

function GitHubIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M12 0C5.37 0 0 5.37 0 12c0 5.31 3.435 9.795 8.205 11.385.6.105.825-.255.825-.57 0-.285-.015-1.23-.015-2.235-3.015.555-3.795-.735-4.035-1.41-.135-.345-.72-1.41-1.23-1.695-.42-.225-1.02-.78-.015-.795.945-.015 1.62.87 1.845 1.23 1.08 1.815 2.805 1.305 3.495.99.105-.78.42-1.305.765-1.605-2.67-.3-5.46-1.335-5.46-5.925 0-1.305.465-2.385 1.23-3.225-.12-.3-.54-1.53.12-3.18 0 0 1.005-.315 3.3 1.23.96-.27 1.98-.405 3-.405s2.04.135 3 .405c2.295-1.56 3.3-1.23 3.3-1.23.66 1.65.24 2.88.12 3.18.765.84 1.23 1.905 1.23 3.225 0 4.605-2.805 5.625-5.475 5.925.435.375.81 1.095.81 2.22 0 1.605-.015 2.895-.015 3.3 0 .315.225.69.825.57A12.02 12.02 0 0024 12c0-6.63-5.37-12-12-12z" />
    </svg>
  );
}

function GoogleIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" aria-hidden="true">
      <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4" />
      <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
      <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
      <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
    </svg>
  );
}

function GitLabIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="#FC6D26" aria-hidden="true">
      <path d="M23.6004 9.5927l-.0337-.0862L20.3 .9814a.851.851 0 00-.3362-.405.8748.8748 0 00-.9997.0539.8748.8748 0 00-.29.4399l-2.2055 6.748H7.5375L5.3318 1.0353a.8573.8573 0 00-.29-.4412.8748.8748 0 00-.9997-.0537.8585.8585 0 00-.3362.405L.4332 9.5015l-.0325.0862a6.0657 6.0657 0 002.0119 7.0105l.0113.0087.03.0213 4.976 3.7264 2.462 1.8633 1.4995 1.1321a1.0085 1.0085 0 001.2197 0l1.4995-1.1321 2.462-1.8633 5.0062-3.7489.0125-.01a6.0682 6.0682 0 002.0094-7.003z" />
    </svg>
  );
}

function BitbucketIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="#2684FF" aria-hidden="true">
      <path d="M.778 1.213a.768.768 0 00-.768.892l3.263 19.81c.084.5.515.868 1.022.873H19.95a.772.772 0 00.77-.646l3.27-20.03a.768.768 0 00-.768-.891zM14.52 15.53H9.522L8.17 8.466h7.561z" />
    </svg>
  );
}

function MicrosoftIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" aria-hidden="true">
      <path d="M1 1h10.5v10.5H1z" fill="#F25022" />
      <path d="M12.5 1H23v10.5H12.5z" fill="#7FBA00" />
      <path d="M1 12.5h10.5V23H1z" fill="#00A4EF" />
      <path d="M12.5 12.5H23V23H12.5z" fill="#FFB900" />
    </svg>
  );
}

function AppleIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" />
    </svg>
  );
}

const PROVIDERS: Array<{
  id: OAuthProvider;
  labelKey: string;
  Icon: (props: { className?: string }) => JSX.Element;
  /** Extra options some providers need (Azure requires the email scope). */
  scopes?: string;
}> = [
  { id: "google", labelKey: "oauth.google", Icon: GoogleIcon },
  { id: "github", labelKey: "oauth.github", Icon: GitHubIcon },
  { id: "gitlab", labelKey: "oauth.gitlab", Icon: GitLabIcon },
  { id: "bitbucket", labelKey: "oauth.bitbucket", Icon: BitbucketIcon },
  { id: "azure", labelKey: "oauth.microsoft", Icon: MicrosoftIcon, scopes: "email" },
  { id: "apple", labelKey: "oauth.apple", Icon: AppleIcon },
];

/** Shown when provider discovery is unreachable: the long-standing set,
 *  never the optimistic full list. */
const BASELINE: OAuthProvider[] = ["google", "github", "gitlab", "bitbucket"];

/** Which providers Supabase Auth reports as enabled. Exported for tests. */
export async function discoverEnabledProviders(): Promise<OAuthProvider[] | null> {
  try {
    const base = getSupabaseUrl();
    if (!base) return null;
    const withTimeout = typeof AbortSignal !== "undefined" && typeof AbortSignal.timeout === "function";
    const res = await fetch(`${base}/auth/v1/settings`, {
      headers: { apikey: getSupabaseAnonKey() },
      ...(withTimeout ? { signal: AbortSignal.timeout(6000) } : {}),
    });
    if (!res.ok) return null;
    const data = (await res.json()) as { external?: Record<string, boolean> };
    if (!data?.external || typeof data.external !== "object") return null;
    return PROVIDERS.map(p => p.id).filter(id => data.external?.[id] === true);
  } catch {
    return null;
  }
}

export function OAuthButtons() {
  const { t } = useI18n();
  const [loadingProvider, setLoadingProvider] = useState<OAuthProvider | null>(null);
  const [visible, setVisible] = useState<OAuthProvider[]>(BASELINE);
  const { toast } = useToast();

  useEffect(() => {
    let cancelled = false;
    discoverEnabledProviders().then(enabled => {
      if (!cancelled && enabled && enabled.length > 0) setVisible(enabled);
    });
    return () => { cancelled = true; };
  }, []);

  const handleOAuth = async (provider: OAuthProvider, scopes?: string) => {
    setLoadingProvider(provider);
    const { error } = await supabase.auth.signInWithOAuth({
      provider,
      options: {
        redirectTo: authRedirectUrl("/auth/callback"),
        ...(scopes ? { scopes } : {}),
      },
    });
    if (error) {
      toast({ variant: "destructive", title: t("oauth.signInFailed"), description: error.message });
      setLoadingProvider(null);
    }
  };

  return (
    <div className="space-y-3">
      {PROVIDERS.filter(p => visible.includes(p.id)).map(({ id, Icon, labelKey, scopes }) => (
        <Button
          key={id}
          type="button"
          variant="outline"
          className="w-full gap-2"
          disabled={!!loadingProvider}
          onClick={() => handleOAuth(id, scopes)}
        >
          {loadingProvider === id ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Icon className="h-4 w-4" />
          )}
          {t(labelKey)}
        </Button>
      ))}
    </div>
  );
}
